import Backbone from '@polleverywhere/backbone'
import _ from 'underscore'
import URI from 'urijs'

const JOB_EVENT_MAP = {
  created: 'create',
  queued: 'queue',
  working: 'inprogress',
  completed: 'complete',
  failed: 'fail'
}

function check (delay) {
  if (delay == null) { delay = this.pollingInterval * 1000 }
  const fetch = () => {
    if (this.isPolling) {
      return this.fetch()
        .done(() => check.call(this))
        .fail((xhr) => {
          resetTimeout.call(this)
          return this.trigger('error', this, xhr)
        })
    } else {
      return resetTimeout.call(this)
    }
  }

  if (delay > 0) {
    this.timeoutId = setTimeout(fetch.bind(this), delay)
  } else {
    return fetch.call(this)
  }
}

function handleStatusChange () {
  const status = this.get('status')
  const eventName = JOB_EVENT_MAP[status]

  switch (status) {
    case 'completed':
    case 'failed':
      this.stopChecking()
      this.trigger(eventName, this.get('data'))
      break
    default:
      this.trigger(eventName, this)
  }
}

function resetTimeout () {
  clearTimeout(this.timeoutId)
  return delete this.timeoutId
}

/*
 * Job models for /api/jobs
 *
 * @namespace Model
 *
 * @event create Job created
 *
 * @event queue Job queued
 *
 * @event inprogress Job in progress
 *
 * @event complete Job complete
 *   @param [Object] data Data returned from job
 *
 * @event fail Job failed due to error
 *   @param [Object] data Data returned from job
 *
 * @event error Error fetching job status
 *   @param [XHR] xhr XHR Request
 *
 */
export default class Job extends Backbone.Model {
  get urlRoot () { return '/api/jobs' }

  get defaults () {
    return {
      status: 'pending'
    }
  }

  get idAttribute () { return 'uuid' }

  /*
   * @param [Object] options
   * @param options [Number] pollingInterval - Set polling interval in seconds
   * @return [Object] The job instance
   *
   */
  initialize (options = {}) {
    _.defaults(options, {
      pollingInterval: 5
    })

    const interval = parseInt(options.pollingInterval)

    if (isNaN(interval)) {
      throw new Error('`options.pollingInterval` must be a number.')
    } else {
      this.pollingInterval = interval
    }

    this.stopChecking()

    this.promise = new Promise((resolve, reject) => {
      this.on('complete', resolve)
      this.on('fail', reject)
    })

    this.on('change:status', handleStatusChange.bind(this))
  }

  /*
   * @return [String]
   *
   */
  status () {
    return this.get('status')
  }

  /*
   * @return [Object] The job instance
   *
   */
  startChecking () {
    this.isPolling = true

    if (!this.timeoutId) {
      check.call(this, 0)
    }

    return this
  }

  /*
   * @return [Object] The job instance
   *
   */
  stopChecking () {
    this.isPolling = false
    return this
  }

  /*
   * Make a job and start checking the progress
   * @param [Object] data Attributes to create the job with
   * @param [String] url Url endpoint to create the job
   *
   * @return [Job] The job instance
   */
  static create (url, data) {
    const job = new Job()

    job.save(data, { url: url })
      .then((data) => {
        job.set({ uuid: URI(data.job_url).segment(-1) })
        job.pollingInterval = 1
        job.startChecking()
      })
      .catch((xhr, status, error) => {
        job.trigger('fail', error)
      })

    return job
  }
}
